import { RosaAuthService } from '@/rosa';
import { Environment } from '@/utils/environment/environment.interface';
import type { primitive } from '@/utils/typing/primitive.type';
import { inject, Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import mixpanel from 'mixpanel-browser';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import type { UserCore } from '../../interfaces/User.interface';
import { Page } from '../../services/routing/routing.service';
import { SESSION_STORAGE_KEYS } from '../local-storage.contants';
import { ProfileProvider } from '../profile/profile.provider';
import { ProjectsProvider } from '../projects/projects.provider';

const mixpanelPageNames: Record<Page, string> = {
    [Page.LOGIN]: 'LoginPage',
    [Page.ACCOUNT_CREATION]: 'AccountCreationPage',
    [Page.DASHBOARDS]: 'BuildingHomeDashboardPage',
    [Page.BUILDING_CREATION]: 'BuildingCreationPage',
    [Page.NO_BUILDING_ACCESS_ERROR]: 'NoBuildingAccessPage',
    [Page.THRESHOLD_SCENARIO_LANDING]: 'ThresholdScenarioLandingPage',
    [Page.THRESHOLD_SCENARIO_EDITION]: 'ThresholdScenarioEditionPage',
    [Page.THRESHOLD_SCENARIO_DETAILS]: 'ThresholdScenarioDetailsPage',
    [Page.WATER_LEAKAGE_SCENARIO_LANDING]: 'WaterLeakageScenarioLandingPage',
    [Page.WATER_LEAKAGE_SCENARIO_EDITION]: 'WaterLeakageScenarioEditionPage',
    [Page.WATER_LEAKAGE_SCENARIO_DETAILS]: 'WaterLeakageScenarioDetailsPage',
    [Page.DRIFT_SCENARIO_LANDING]: 'DriftScenarioLandingPage',
    [Page.DRIFT_SCENARIO_EDITION]: 'DriftScenarioEditionPage',
    [Page.DRIFT_SCENARIO_DETAILS]: 'DriftScenarioDetailsPage',
    [Page.ALERT_SCENARIO_REPORT_PLANS]: 'AlertScenarioReportPlansPage',
    [Page.ACTIONS_LIST]: 'BoardBuildingsActionsDesktopPage',
    [Page.ACTION_CONSULTATION]: 'ActionConsultationPage',
    [Page.AUTOMATED_REPORTS_LIST]: 'AutomatedReportsPage',
    [Page.AUTOMATED_REPORT_EDITION]: 'AutomatedReportsEditionPage',
    [Page.SETTINGS]: 'SettingsPage',
    [Page.USER_PROFILE]: 'ProfileInfoPage',
    [Page.DATA_EXPORT]: 'DataExportPage',
    [Page.MAPS]: 'MapsPage',
    [Page.DATA_VIEWER]: 'DataViewerPage',
    [Page.CONNECTED_AS]: 'ConnectedAsPage',
    [Page.DASHAVIZ]: 'DashavizPage',
    [Page.BUILDINGS]: 'BuildingsPage',
    [Page.BUILDING_DETAILS]: 'BuildingDetailsPage',
    [Page.METER_DETAILS]: 'MeterEditorPage',
    [Page.DELIVERY_POINTS]: 'DeliveryPointsPage',
    [Page.USERS_PROFILES_MANAGEMENT]: 'AdminUsersProfilesPage',
    [Page.CLASSIFICATIONS_MANAGEMENT]: 'ClassificationsPage',
    [Page.PROJECT_SETTINGS]: 'AdminSettingsPage',
    [Page.ATTRIBUTES_MANAGEMENT]: 'AttributeManagementPage',
    [Page.IPMVP]: 'AdminIpmvpPage',
    [Page.IMPORT_HISTORY]: 'AdminImportHistoryPage',
    [Page.BUSINESS_INDICATORS_DETAILS]: 'BusinessIndicatorsDetailsPage',
    [Page.BUSINESS_INDICATORS_MANAGEMENT]: 'AdminBusinessIndicatorsPage',
    [Page.BUSINESS_INDICATOR_EDITION]: 'BusinessIndicatorConfigurationPage',
    [Page.BUSINESS_INDICATOR_REFERENCE_PERIODS_EDITION]: 'ReferencePeriodBusinessIndicatorPage',
    [Page.ENTITY_MANAGEMENT]: 'ManagedEntityPage',
    [Page.DATA_HEALTH_SYNTHESIS]: 'DataHealthSynthesis',
    [Page.DATA_HEALTH_TEMPORAL_COMPLETENESS]: 'DataHealthTemporalCompleteness',
    [Page.DATA_HEALTH_TEMPORAL_COMPLETENESS_DETAILS]: 'DataHealthTemporalCompletenessDetails',
    [Page.DATA_HEALTH_COMPLETENESS]: 'DataHealthCompleteness',
    [Page.DATA_HEALTH_QUALITY]: 'DataHealthQuality',
    [Page.DATA_QUALIFICATION_COMPLETENESS_DETAILS]: 'DataQualityCompletenessDetailsPage',
    [Page.DOCUMENT_MANAGEMENT]: 'DocumentManagementPage',
    [Page.DOCUMENT_MANAGEMENT_IMPORT]: 'DocumentManagementImportPage',
    [Page.INDICATORS_BUILDINGS_COMPARISON]: 'IndicatorsBuildingsComparisonPage',
    [Page.SYSTEM_EDITION]: 'SystemEditionPage',
    [Page.VIRTUAL_SYSTEM_EDITION]: 'VirtualSystemEditionPage',
    [Page.VIRTUAL_SENSOR_EDITION]: 'VirtualSensorEditionPage',
    [Page.CUSTOM_TILE_CONFIGURATION]: 'TileCreationMenuPage',
    [Page.MULTI_SENSOR_TILE_CREATION]: 'TileCreationMultiSensorPage',
    [Page.SYSTEM_ADD]: 'SystemAddPage',
    [Page.SYSTEM_ADD_ADVANCED]: 'SystemAddAdvancedPage',
    [Page.VIRTUAL_SYSTEM_ADD]: 'VirtualSystemAddPage',
    [Page.CONFIRM_PASSWORD]: 'ConfirmPasswordPage',
    [Page.MANUAL_INDEX_READINGS]: 'ManualIndexReadingsPage',
    [Page.SPECIFIC_MANUAL_INDEX_READINGS]: 'SpecificManualIndexReadingsPage',
    [Page.BILLS_HOME]: 'BillsHomePage',
    [Page.BILLS_BY_BUILDING]: 'BillsByBuildingPage',
    [Page.BILLS_BY_BUILDING_ANALYSIS]: 'BillsByBuildingAnalysisPage',
    [Page.BILLS_BY_DELIVERY_POINT]: 'BillsByDeliveryPointPage',
    [Page.DELIVERY_POINT_ADD]: 'DeliveryPointAddPage',
    [Page.SUPPLIER_PORTAL_ACCESS]: 'SupplierPortalAccessPage',
    [Page.BILLS_IMPORT_FILES]: 'BillsImportFilesPage',
    [Page.INVOICE_DISCREPANCY]: 'InvoiceDiscrepancyPage',
    [Page.INVOICE_EDITION]: 'InvoiceEditionPage',
    [Page.INVOICE_ERROR]: 'InvoiceErrorPage',
    [Page.METERS_LIST]: 'MetersListPage',
    [Page.METER_INDEX_INIT]: 'MeterIndexInitPage',
    [Page.METERS_INDEX_STATEMENT]: 'MeterIndexStatementPage',
    [Page.BUDGET_FUNNEL_VIEW]: 'BudgetFunnelViewPage',
    [Page.BUDGET_EDITION]: 'BudgetEditionPage',
    [Page.CONTRACT_POWER_OPTIMIZATION]: 'ContractPowerOptimizationPage',
    [Page.SUPPLIERS_MISSING_INVOICES]: 'SuppliersMissingInvoicesPage',
    [Page.IPE_HISTORY]: 'IpeHistoryPage',
    [Page.TEST_DATASOURCE]: 'TestDatasourcePage',
    [Page.TEST_DASHBOARD]: 'DashboardPage',
    [Page.BASELOAD_ANALYSIS]: 'BuildingsAnalyzePage',
    [Page.BUILDING_BASELOAD_ANALYSIS]: 'BuildingAnalyzePage',
    [Page.CUSTOM_BUSINESS_INTELLIGENCE_ANALYSIS]: 'CustomBusinessIntelligenceAnalysisPage',
    [Page.DATABOARDS]: 'DataboardsPage',
    [Page.EXPORT_LANDING]: 'ExportLandingPage',
    [Page.EXPORT_EDITION]: 'ExportEditionPage',
    [Page.GENERAL_CONDITIONS]: 'GeneralConditionsPage',
    [Page.PASSWORD_EXPIRED]: 'PasswordExpiredPage',
    [Page.HOMEPAGE]: 'HomepagePage',
    [Page.SEN_ANALYSIS_LANDING]: 'SenAnalysisLandingPage',
    [Page.SEN_ANALYSIS]: 'SenAnalysisPage',
    [Page.DEET_EFA_LANDING]: 'DeetEfaLandingPage',
    [Page.RECOMMENDED_ACTIONS_LIST]: 'RecommendedActionsLandingPage',
    [Page.IOT_SUPERVISION]: 'IotSupervision',
    [Page.MIP]: 'MIPLandingPage',
    [Page.MIP_PLAN_EDIT]: 'MIPPlanEditionPage',
    [Page.MIP_SCENARIO_SYNTHESIS]: 'MIPScenarioSynthesisPage',
    [Page.ENERGY_SIGNATURE]: 'EnergySignaturePage',
    [Page.THERMAL_COMFORT]: 'ThermalComfortPage'
};

@Injectable({
    providedIn: 'root'
})
export class TrackingService {
    private profileProvider = inject(ProfileProvider);
    private translate = inject(TranslateService);
    private _projectsProvider = inject(ProjectsProvider);
    private _zone = inject(NgZone);
    private _env = inject(Environment);
    private _rosaAuthService = inject(RosaAuthService);

    isInitialized$ = new BehaviorSubject(false);

    init() {
        if (this._env.currentMode === 'dev') {
            console.debug('Init mixpanel with token : ' + this._env.mixPanelToken);
            console.debug('Mixpanel activated ?', this._env.isMixPanelActivated);
        }
        if (this.isTrackingAllowed()) {
            this._zone.runOutsideAngular(() => {
                mixpanel.init(this._env.mixPanelToken);
                this.isInitialized$.next(true);
            });
        }
    }

    isInitialized(): Promise<boolean> {
        return firstValueFrom(this.isInitialized$.pipe(filter(isInitialized => isInitialized)));
    }

    sessionStart() {
        this.trackUserAction('session time', {});
    }

    sessionEnd() {
        return this.trackUserAction('session time', {});
    }

    pageView(page: Page, optionalProperties: Object = {}) {
        this.trackUserAction('page view', {
            'view name': mixpanelPageNames[page],
            ...optionalProperties
        });
    }

    widgetViewed(widgetName: string, optionalProperties: Object = {}) {
        this.trackUserAction('widget loaded', {
            'widget name': widgetName,
            ...optionalProperties
        });
    }

    trackEvent(pageName: string, eventName: string, optionalProperties: Object = {}) {
        this.trackUserAction('user action', {
            'view name': pageName,
            'action name': eventName,
            ...optionalProperties
        });
    }

    trackClick(name: string, additionalProperties: Record<string, primitive>) {
        this.trackUserAction('click', {
            name,
            ...additionalProperties
        });
    }

    private async trackUserAction(actionName: string, properties: Object = {}) {
        if (this.isTrackingAllowed()) {
            await this.isInitialized();

            const project = this._projectsProvider.getUserCurrentProject();

            return mixpanel.track(actionName, {
                projectId: project ? project.projectId : null,
                projectName: project ? project.name : null,
                ...properties
            });
        }
    }

    private isDoNotTrack() {
        // The browser supports Do Not Track!
        if (
            (<any>window).doNotTrack ||
            (<any>navigator).doNotTrack ||
            (<any>navigator).msDoNotTrack ||
            (window && window.external && 'msTrackingProtectionEnabled' in window.external)
        ) {
            // Do Not Track is enabled!
            if (
                (<any>window).doNotTrack === '1' ||
                (<any>navigator).doNotTrack === 'yes' ||
                (<any>navigator).doNotTrack === '1' ||
                (<any>navigator).msDoNotTrack === '1' ||
                ('msTrackingProtectionEnabled' in window.external && (<any>window.external).msTrackingProtectionEnabled())
            ) {
                return true;
            }
            // Do Not Track is disabled!
            else return false;
        }
        // Do Not Track is not supported
        else return false;
    }

    async identifyUser() {
        if (!this._env.isMixPanelActivated || !this.isTrackingAllowed()) {
            return;
        }
        // init the Mixpanel service

        await this.isInitialized();

        const currentUser = this.profileProvider.getUser();

        mixpanel.identify(currentUser.id);
        mixpanel.identify(currentUser.id);

        // Set people properties
        mixpanel.people.set({
            $adv_app_channel: this._env.currentMode,
            $app_mode: this._env.isEnergyManager ? 'energy manager' : 'client',
            $language: this.translate.getCurrentLang(),

            $adv_user_uid: currentUser.id,
            $first_name: currentUser.firstname,
            $last_name: currentUser.lastname,
            $email: currentUser.email,
            $company: currentUser.company,
            $job_title: currentUser.position,
            $phone_number: currentUser.phoneNumber
        });
    }

    isTrackingAllowed(): boolean {
        const currentUser = this.profileProvider.getUser();
        return (
            this._env.isMixPanelActivated &&
            !this.isDoNotTrack() &&
            currentUser &&
            currentUser.isUserTracked &&
            !this.isUserPartOfOrange(currentUser) &&
            sessionStorage.getItem(SESSION_STORAGE_KEYS.REPORT_PRINT_SESSION) !== 'true' &&
            !this._rosaAuthService.isConnectedAs()
        );
    }

    private isUserPartOfOrange(user: UserCore): boolean {
        return user.email.includes('@orange.com');
    }
}
